import { useRef } from 'react';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ConfirmButtonGroup } from '#components/patterns/confirmations/confirm-button-group/ConfirmButtonGroup.tsx';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '#components/patterns/modal/index.ts';
import { ALL_ROLES_FLAG } from '#screens/roles/detail/firewall-rule-form/FirewallRuleForm.queries.ts';
import { FirewallRuleForm } from '../firewall-rule-form/FirewallRuleForm.tsx';
import type { FirewallRuleFieldValue } from '../types.ts';

type Props = {
  onSave: (newFw: FirewallRuleFieldValue) => void;
  onClose: () => void;
  /** Prefills the form, e.g. when duplicating an existing rule */
  defaultValues?: FirewallRuleFieldValue;
};

/**
 * {@link AddFirewallRuleModal} shows a form for adding a new firewall rule
 */
export function AddFirewallRuleModal({ onSave, onClose, defaultValues }: Props) {
  const modalRef = useRef(null);

  const newFirewallRule =
    defaultValues ??
    ({
      protocol: 'ANY',
      portRange: null,
      allowedRoleID: ALL_ROLES_FLAG,
      tags: [],
      description: '',
      id: '',
    } satisfies FirewallRuleFieldValue);

  return (
    <Modal ref={modalRef} isOpen={true} onClose={onClose}>
      <ModalHeader>Add inbound firewall rule</ModalHeader>
      <ModalBody>
        <FirewallRuleForm
          rule={newFirewallRule}
          onSave={(values) => {
            onSave(values);
          }}
        />
      </ModalBody>
      <ModalFooter>
        <ConfirmButtonGroup
          variant="right"
          primaryButton={
            <Button type="submit" form="firewall-rule-form">
              Add rule
            </Button>
          }
          secondaryButton={
            <Button variant="link" onClick={() => onClose()}>
              Cancel
            </Button>
          }
        />
      </ModalFooter>
    </Modal>
  );
}
